import React, { useEffect, useState } from 'react';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { InboxMessage } from '../types/api';
import { apiService } from '../services/api';

function formatFullDate(dateString: string): string {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function navigateToLink(link: string): void {
  if (/^https?:\/\//i.test(link)) {
    window.open(link, '_blank', 'noopener,noreferrer');
    return;
  }

  const target = link.startsWith('/') ? link : `/${link}`;
  window.history.pushState({}, '', target);
  window.dispatchEvent(new CustomEvent('urlchange'));
}

function getLinkHref(link: string): string {
  if (/^https?:\/\//i.test(link)) {
    return link;
  }

  return link.startsWith('/') ? link : `/${link}`;
}

function renderMarkdownLinks(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const pattern = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    const [fullMatch, label, url] = match;
    const start = match.index;

    if (start > lastIndex) {
      nodes.push(text.slice(lastIndex, start));
    }

    const href = getLinkHref(url.trim());
    const isExternal = /^https?:\/\//i.test(url.trim());

    nodes.push(
      <a
        key={`${start}-${fullMatch}`}
        href={href}
        onClick={(event) => {
          if (!isExternal) {
            event.preventDefault();
            navigateToLink(url.trim());
          }
        }}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        className="text-[#132B64] font-semibold underline hover:opacity-80 transition-opacity"
      >
        {label}
      </a>
    );

    lastIndex = start + fullMatch.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}

interface InboxDetailViewProps {
  messageId: string;
  onTitleChange?: (title: string | null) => void;
  onInboxChanged?: () => void;
}

export const InboxDetailView: React.FC<InboxDetailViewProps> = ({
  messageId,
  onTitleChange,
  onInboxChanged,
}) => {
  const [message, setMessage] = useState<InboxMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadMessage() {
      setLoading(true);
      setError(null);

      try {
        const inboxMessage = await apiService.getInboxMessage(messageId);
        if (cancelled) {
          return;
        }
        setMessage(inboxMessage);
        onTitleChange?.('Message Details');
        onInboxChanged?.();
      } catch (err) {
        if (cancelled) {
          return;
        }
        setError(err instanceof Error ? err.message : 'Failed to load inbox message');
        onTitleChange?.('Inbox');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadMessage();

    return () => {
      cancelled = true;
    };
  }, [messageId, onInboxChanged, onTitleChange]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#132B64]" />
      </div>
    );
  }

  if (error || !message) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4 px-4">
        <AlertTriangle size={48} className="text-red-400" />
        <p className="text-gray-700 text-center">{error || 'Message not found'}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24 lg:pb-8">
        <div className="w-full max-w-[320px] lg:max-w-[620px] mx-auto">
          <div className="bg-[#E7EEE3] rounded-[24px] shadow-sm p-5 sm:p-6">
            <div className="flex items-center justify-between gap-3 mb-4">
              <span className="inline-flex items-center px-3 py-1.5 bg-white/70 rounded-full text-xs font-semibold text-[#132B64]">
                {message.is_read ? 'Read' : 'Unread'}
              </span>
              <span className="text-xs text-gray-500">
                {formatFullDate(message.created_at)}
              </span>
            </div>

            <h1 className="text-xl font-bold text-[#132B64] leading-tight">
              {message.title}
            </h1>

            <div className="mt-4 text-sm text-gray-700 whitespace-pre-wrap leading-6">
              {renderMarkdownLinks(message.body)}
            </div>

            {message.link && (
              <div className="mt-6">
                <button
                  onClick={() => navigateToLink(message.link!)}
                  className="inline-flex items-center gap-2 bg-[#132B64] text-white rounded-full px-5 py-3 text-sm font-semibold hover:bg-[#0f2350] transition-colors"
                >
                  Open link
                  <ExternalLink size={14} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
